/**
 * Камера поля: изометрия 2:1, та же, что у атласа (`tools/voxel/README.md`, «Сцена»).
 *
 * Поле боя (`x` поперёк, `y` от штаба к краю, `FieldGeometry`) лежит на экране осями: `x` — вправо, `y` — вверх со
 * сжатием 1/2. Это ровно взгляд изометрической камеры атласа на землю, повёрнутую на 45°: экранное «вправо» — северо-восток
 * модели, «вверх» — северо-запад. Поэтому прямоугольник поля вписывается в экран целиком (телефон и десктоп), а спрайт
 * выбирается по направлению модели, повёрнутому на 45° ({@link toSpriteDirection}).
 *
 * Масштаб: `scale` — пикселей экрана на клетку поля по горизонтали; клетка модели (ромб шириной `tilePixels` в атласе)
 * на экране шириной `scale · √2` ({@link spriteScale}).
 */
import type { Direction, FieldGeometry } from "@newfungame/contracts";
import { FIXED_ONE } from "@newfungame/contracts";

export const DIRECTIONS: readonly Direction[] = ["n", "ne", "e", "se", "s", "sw", "w", "nw"];

/** Отступы под панели оболочки (десктоп: герои слева, задания справа; телефон: шапка и вкладки), px CSS. */
export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const NO_INSETS: Insets = Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 });

export interface Camera {
  /** Пикселей экрана на клетку поля по горизонтали. */
  scale: number;
  /** Экранная точка мировой (0, 0). */
  originX: number;
  originY: number;
  /** Раздвинуть мир вокруг штаба только на экране ({@link RadialWarp}); нет — без искажений. */
  warp?: RadialWarp;
}

/**
 * Радиальное растяжение вокруг точки (штаба) — только отрисовка, рука Г A/B-теста масштаба поля (MADR
 * 0035): камера дальше, а штаб, платформы и герои прежнего размера; чтобы крупный штаб не наезжал на
 * платформы, расстояния до `r0` растут в `k` раз, от `r0` до `r1` прибавка плавно сходит на нет, дальше мир как есть.
 * Монотонно и обратимо при `r1 > k · r0`. Единицы — мир (фиксированная точка).
 */
export interface RadialWarp {
  cx: number;
  cy: number;
  k: number;
  r0: number;
  r1: number;
}

/** Расстояние от центра на экране (в мировых единицах) по настоящему расстоянию `d`. */
export function warpDistance(w: RadialWarp, d: number): number {
  const extra = (w.k - 1) * w.r0;
  if (d <= w.r0) return d * w.k;
  if (d >= w.r1) return d;
  return d + (extra * (w.r1 - d)) / (w.r1 - w.r0);
}

/** Обратное к {@link warpDistance}. */
export function unwarpDistance(w: RadialWarp, d: number): number {
  const extra = (w.k - 1) * w.r0;
  if (d <= w.r0 * w.k) return d / w.k;
  if (d >= w.r1) return d;
  // d = x · (1 − e/(r1 − r0)) + e · r1/(r1 − r0)
  const a = 1 - extra / (w.r1 - w.r0);
  return (d - (extra * w.r1) / (w.r1 - w.r0)) / a;
}

function warpPoint(w: RadialWarp, x: number, y: number, inverse: boolean, out: Point): Point {
  const dx = x - w.cx;
  const dy = y - w.cy;
  const d = Math.hypot(dx, dy);
  const m = d === 0 ? 1 : (inverse ? unwarpDistance(w, d) : warpDistance(w, d)) / d;
  out.x = w.cx + dx * m;
  out.y = w.cy + dy * m;
  return out;
}

export interface FitOptions {
  insets?: Insets;
  /** Запас сверху над краем поля, клеток: спрайты зомби на дальнем краю торчат вверх. */
  topMargin?: number;
  /** Не крупнее: спрайт не растягивается больше 1:1 к атласу (`tilePixels / √2`). */
  maxScale?: number;
  /**
   * Какую долю ширины поля можно обрезать по бокам, если свободная часть экрана — портрет (телефон): поле 9 × 16 в изометрии
   * почти квадратное, и без обрезки на телефоне всё мелкое. Зомби появляются на боковых краях за экраном и входят в
   * кадр. По умолчанию 0,2.
   */
  crop?: number;
  /** Множитель итогового масштаба (<1 — всё мельче, поле видно с запасом). По умолчанию 1. */
  zoom?: number;
  warp?: RadialWarp;
}

/** Камера, вписывающая поле в экран за вычетом отступов; поле — по центру свободной части. */
export function fitCamera(field: FieldGeometry, width: number, height: number, opts: FitOptions = {}): Camera {
  const ins = opts.insets ?? NO_INSETS;
  const top = opts.topMargin ?? 1;
  const availW = Math.max(1, width - ins.left - ins.right);
  const availH = Math.max(1, height - ins.top - ins.bottom);
  const cols = field.width / FIXED_ONE;
  const rows = field.depth / FIXED_ONE / 2 + top / 2;
  const crop = Math.min(0.5, Math.max(0, opts.crop ?? 0.2));
  let scale = Math.min(availW / cols, availH / rows);
  // портрет (телефон): крупнее за счёт боков, пока высота позволяет; десктоп между панелями — без обрезки
  if (availW < availH && availW / cols < availH / rows) scale = Math.min(availW / (cols * (1 - crop)), availH / rows);
  if (opts.maxScale) scale = Math.min(scale, opts.maxScale);
  scale = Math.max(4, scale * (opts.zoom ?? 1));
  const cx = ins.left + availW / 2;
  const cy = ins.top + availH / 2;
  // центр поля (0, depth/2) — в центр свободной части, сдвинутый вниз на половину верхнего запаса
  const originY = cy + ((field.depth / FIXED_ONE / 2) * scale) / 2 + (top * scale) / 4;
  return opts.warp ? { scale, originX: cx, originY, warp: opts.warp } : { scale, originX: cx, originY };
}

export interface Point {
  x: number;
  y: number;
}

/** Мировая точка (единицы фиксированной точки) → экранная; пишет в `out`, чтобы не создавать объекты каждый кадр. */
export function worldToScreen(cam: Camera, x: number, y: number, out: Point = { x: 0, y: 0 }): Point {
  if (cam.warp) ({ x, y } = warpPoint(cam.warp, x, y, false, out));
  out.x = cam.originX + (x / FIXED_ONE) * cam.scale;
  out.y = cam.originY - (y / FIXED_ONE) * (cam.scale / 2);
  return out;
}

/** Экранная точка → мировая (на земле). */
export function screenToWorld(cam: Camera, sx: number, sy: number, out: Point = { x: 0, y: 0 }): Point {
  out.x = ((sx - cam.originX) / cam.scale) * FIXED_ONE;
  out.y = ((cam.originY - sy) / (cam.scale / 2)) * FIXED_ONE;
  return cam.warp ? warpPoint(cam.warp, out.x, out.y, true, out) : out;
}

/** Масштаб спрайта: кадр атласа с `tilePixels` на клетку → экран. */
export function spriteScale(cam: Camera, tilePixels: number): number {
  return (cam.scale * Math.SQRT2) / tilePixels;
}

/** Слои при одинаковой глубине: платформа под героем, штаб под пушкой, снаряд и эффект — поверх. */
export const Layer = {
  ground: 0,
  unit: 1,
  mounted: 2,
  projectile: 3,
  effect: 4,
} as const;

/**
 * Порядок отрисовки (больше — позже, ближе к зрителю): ниже на экране — ближе. Ключ — от мировой `y`
 * (меньше `y` → ниже на экране → позже), внутри одной глубины — по слою.
 */
export function depthOf(y: number, layer: number = Layer.unit): number {
  return Math.round(-y) * 8 + layer;
}

/**
 * Глубина облака взрыва (ISSUE-125): по ближнему к камере краю, а не по центру. Ближний паркан рисуется полосами, у каждой
 * своя глубина; крупное облако с глубиной центра ложилось поверх дальних полос и под ближние — прямой вертикальной границей.
 * Край — центр минус полуширина спрайта: паркан идёт по диагонали, на `d` клеток вбок он ближе на те же `d` клеток.
 */
export function cloudDepth(y: number, halfWidthPx: number, scale: number, layer: number = Layer.effect): number {
  return depthOf(y - (halfWidthPx / Math.max(1, scale)) * FIXED_ONE, layer);
}

/**
 * Направление движения в мире поля по смещению; `null` — почти на месте (оставить прежнее).
 * Октанты по 45°, `n` — к `+y`.
 */
export function directionFromDelta(dx: number, dy: number, epsilon = 1): Direction | null {
  if (dx * dx + dy * dy < epsilon * epsilon) return null;
  // угол от +y по часовой: n=0, ne=1, e=2, …
  const a = Math.atan2(dx, dy);
  const i = Math.round((a / (Math.PI / 4) + 8) % 8) % 8;
  return DIRECTIONS[i]!;
}

/**
 * Направление поля → направление спрайта модели. Поле повёрнуто к модели на 45°: «вправо» поля (`e`) — это северо-восток
 * модели (`ne`), «вверх» (`n`) — северо-запад (`nw`).
 */
export function toSpriteDirection(d: Direction): Direction {
  const i = DIRECTIONS.indexOf(d);
  return DIRECTIONS[(i + 7) % 8]!;
}
